import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useScrollContext } from '../context/ScrollContext';

// Register plugin once
gsap.registerPlugin(ScrollTrigger);

/**
 * Custom hook for GSAP ScrollTrigger animations
 *
 * @param {React.RefObject} targetRef - Element to animate
 * @param {Object} config - Animation configuration
 * @param {Object} config.from - Starting animation values (optional, use for fromTo)
 * @param {Object} config.to - Ending animation values (required)
 * @param {Object} config.scrollTrigger - ScrollTrigger options (trigger, start, end, etc.)
 * @param {React.RefObject} config.triggerRef - Custom trigger element (defaults to targetRef)
 * @param {Array} deps - Dependency array for useEffect
 */
export const useScrollTrigger = (targetRef, config, deps = []) => {
  const scrollerRef = useScrollContext();

  useEffect(() => {
    if (!targetRef?.current) return;

    const { from, to, scrollTrigger = {}, triggerRef } = config;

    const scrollTriggerConfig = {
      trigger: triggerRef?.current || targetRef.current,
      scroller: scrollerRef?.current,
      ...scrollTrigger,
    };

    const ctx = gsap.context(() => {
      if (from) {
        gsap.fromTo(targetRef.current, from, {
          ...to,
          scrollTrigger: scrollTriggerConfig,
        });
      } else {
        gsap.to(targetRef.current, {
          ...to,
          scrollTrigger: scrollTriggerConfig,
        });
      }
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, scrollerRef, ...deps]);
};

/**
 * Preset: Fade in animation
 */
export const useScrollFadeIn = (targetRef, options = {}) => {
  const {
    start = 'top-=300 top',
    end = 'top top',
    duration = 0.5,
    ...rest
  } = options;

  useScrollTrigger(targetRef, {
    from: { opacity: 0 },
    to: {
      opacity: 1,
      duration,
      ease: 'none',
    },
    scrollTrigger: {
      start,
      end,
      toggleActions: 'play none none reverse',
      ...rest,
    },
  });
};

/**
 * Preset: Slide in from direction
 */
export const useScrollSlideIn = (targetRef, options = {}) => {
  const {
    direction = 'left',
    distance = 500,
    start = 'top-=300 top',
    end = 'top top',
    duration = 0.5,
    ease = 'power3',
    ...rest
  } = options;

  const axis = direction === 'left' || direction === 'right' ? 'x' : 'y';
  const value = direction === 'left' || direction === 'up' ? -distance : distance;

  useScrollTrigger(targetRef, {
    from: { [axis]: value, opacity: 0 },
    to: {
      [axis]: 0,
      opacity: 1,
      duration,
      ease,
    },
    scrollTrigger: {
      start,
      end,
      toggleActions: 'play none none reverse',
      ...rest,
    },
  });
};

/**
 * Preset: Parallax/scrub animation
 * @param scrollerRefOverride - Optional ref to bypass context (use when calling from ScrollProvider host)
 */
export const useScrollParallax = (targetRef, options = {}, scrollerRefOverride = null) => {
  const contextScrollerRef = useScrollContext();
  const scrollerRef = scrollerRefOverride || contextScrollerRef;

  const {
    to = {},
    triggerRef,
    start = 'top bottom',
    end = 'bottom top',
    scrub = true,
    ...rest
  } = options;

  useEffect(() => {
    if (!targetRef?.current) return;

    const scrollTriggerConfig = {
      trigger: triggerRef?.current || targetRef.current,
      scroller: scrollerRef?.current,
      start,
      end,
      scrub,
      ...rest,
    };

    const ctx = gsap.context(() => {
      gsap.to(targetRef.current, {
        ease: 'none',
        ...to,
        scrollTrigger: scrollTriggerConfig,
      });
    });

    return () => ctx.revert();
    // Animation config is intentionally excluded - we only set up once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, scrollerRef]);
};

export default useScrollTrigger;
